// INVCOUNT2-S2 (docs/build-briefs/INVCOUNT2-S2-count-table-screen.md, PRD P0.7, DEC-409). The table-based stock count
// screen: one row per item in saved shelf order, location tabs, cases and bottles and tenths entry, a move sheet, a
// Find item list, the Blind switch, and Review and Close.
//
// Touch only. There is no text input anywhere in this file; every value is a stepper, a picker or a button, and every
// target is at least --touch-target-min (see the .ct- rules in styles.css). Counts are observations: each tap saves the
// row through POST .../table-counts, and nothing posts to on-hand until Close (the server's closeCountSession).
//
// Blind (INV24). In a blind session a row has no Last count cell at all and the table read carries no last count; the
// DOM never holds a figure it was not sent. Review is the one screen that shows expected, exactly as the walk's does.
//
// admin.ts owns the session, permissions, approval flow and the walk; this module is handed them through
// CountTableContext and never reaches into admin.ts itself.

import {
  getCountSessionReview,
  getCountSessionTable,
  getCountSessionUncounted,
  getCountSessionWalk,
  listCountPlacements,
  moveCountPlacement,
  recordCount,
  recordCountTableEntry,
  setCountPlacement,
  setCountSessionMode,
  type CountPlacementMove,
  type CountSessionDto,
  type CountTableDto,
} from "./api.ts";
import { categoryLabel, containerTypeLabel } from "./catalogDisplay.ts";
import { buildAreaScreen, type AreaHost } from "./countArea.ts";
import {
  areaRows,
  areaTabs,
  firstOpenArea,
  kegEntry,
  nextUncountedId,
  type KegCount,
} from "./countAreaModel.ts";
import { countHeldByOtherMessage, plainCountMessage } from "./countWalk.ts";
import {
  ALL_TAB,
  UNASSIGNED_TAB,
  alphabetLetters,
  buildTabs,
  countedTotals,
  entriesEqual,
  entryTotalText,
  formatHundredths,
  filterReviewRows,
  firstLetter,
  isDifference,
  nextTab,
  pickerValues,
  reviewRowFromCounted,
  reviewRowFromUncounted,
  closedSummary,
  reviewValueTotals,
  signedMoney,
  signedPercent,
  rowFromItem,
  setEntryTenths,
  shelfPosition,
  sortReviewRows,
  stepEntry,
  tabCountText,
  tabIdForRow,
  totalHundredths,
  visibleRows,
  zeroEntry,
  TENTHS_OPTIONS,
  type EntryField,
  type ReviewFilter,
  type CountClosedSummary,
  type ReviewRow,
  type TableEntry,
  type TableFilters,
  type TableRow,
  type TableSort,
} from "./countTableModel.ts";
import { COUNT_CHECK_PERCENT, dollarVariance } from "../../src/domain/countReviewValue.ts";
import { compareNames } from "./nameSort.ts";
import { formatMoney } from "./reportFormat.ts";
import type { Session } from "./session.ts";

/** The device's saved choice of count screen. Absent means the table. */
export const COUNT_VIEW_STORAGE_KEY = "bms-count-view";

export function readCountViewPreference(): "table" | "walk" {
  try {
    return window.localStorage.getItem(COUNT_VIEW_STORAGE_KEY) === "walk" ? "walk" : "table";
  } catch {
    return "table";
  }
}

export function writeCountViewPreference(view: "table" | "walk"): void {
  try {
    if (view === "walk") window.localStorage.setItem(COUNT_VIEW_STORAGE_KEY, "walk");
    else window.localStorage.removeItem(COUNT_VIEW_STORAGE_KEY);
  } catch {
    // Storage unavailable: the table stays the default, which is the safe answer.
  }
}

/** INVDESIGN1-S1: the device's saved choice of how the count screen is laid out. Absent means the area-by-area count. */
export const COUNT_LAYOUT_STORAGE_KEY = "bms-count-layout";

export function readCountLayoutPreference(): "table" | "area" {
  try {
    return window.localStorage.getItem(COUNT_LAYOUT_STORAGE_KEY) === "table" ? "table" : "area";
  } catch {
    return "area";
  }
}

export function writeCountLayoutPreference(layout: "table" | "area"): void {
  try {
    if (layout === "table") window.localStorage.setItem(COUNT_LAYOUT_STORAGE_KEY, "table");
    else window.localStorage.removeItem(COUNT_LAYOUT_STORAGE_KEY);
  } catch {
    // Storage unavailable: the area count stays the default.
  }
}

export interface CountTableContext {
  /** The signed-in session, or undefined once it has gone. */
  session(): Session | undefined;
  hasPermission(permission: string): boolean;
  /** False once the admin screen has been left or the sign-in has changed. */
  isCurrent(): boolean;
  toast(message: string, variant: "success" | "danger"): void;
  itemKindLabel(itemKind: string): string;
  actorName(displayName: string | null): string;
  /** The element the screen is built in. */
  mount: HTMLElement;
  lockScroll(): void;
  unlockScroll(): void;
  /** The audited reassignment to this user. Undefined when it did not happen. */
  takeOver(sessionId: string, holderName: string): Promise<CountSessionDto | undefined>;
  /** Close with the approval flow. Undefined when it was cancelled or refused. */
  closeCount(sessionId: string): Promise<{ adjustmentCount: number } | undefined>;
  /** Saved and exited: the count stays open. */
  onExit(): void;
  /** The close report was dismissed: the count is closed. INVDESIGN1-S2: what the review said at the moment of closing, for
   * the after-count screen. */
  onClosed(summary: CountClosedSummary): void;
  /** Switch this device to the one-at-a-time walk. */
  openWalk(sessionId: string): void;
  publishRoute(): void;
}

interface TableState {
  ctx: CountTableContext;
  sessionId: string;
  blind: boolean;
  assignedCounterEmployeeId: string;
  assignedCounterName: string;
  rows: TableRow[];
  sections: { id: string; name: string }[];
  filters: TableFilters;
  currentId: string | undefined;
  /** INVDESIGN1-S1: the table or the area-by-area layout. In the area layout filters.tabId is the area and currentId
   * is the active item. */
  layout: "table" | "area";
  showLast: boolean;
  /** The quarters on tap the counter picked per keg, which the saved total alone cannot say. */
  kegTaps: Map<string, number>;
  view: "count" | "review";
  reviewRows: ReviewRow[];
  reviewFilter: ReviewFilter;
  saving: Set<string>;
  inflight: Set<Promise<void>>;
  busy: boolean;
  root: HTMLElement;
}

let state: TableState | undefined;
/** Bumped on every open and teardown so a slow response for a closed screen is dropped. */
let sequence = 0;

export function countTableSessionId(): string | undefined {
  return state?.sessionId;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(
  className: string,
  label: string,
  testId: string,
  onTap: () => void,
  ariaLabel?: string,
): HTMLButtonElement {
  const node = el("button", className, label);
  node.type = "button";
  node.dataset["testid"] = testId;
  if (ariaLabel !== undefined) node.setAttribute("aria-label", ariaLabel);
  node.addEventListener("click", onTap);
  return node;
}

function current(token: number): TableState | undefined {
  return state !== undefined && token === sequence && state.ctx.isCurrent() ? state : undefined;
}

// ---------------------------------------------------------------------
// Open, close
// ---------------------------------------------------------------------

function applyTable(
  body: CountTableDto,
  ctx: CountTableContext,
): Omit<TableState, "ctx" | "root" | "sessionId"> {
  const observations = new Map(body.observations.map((o) => [o.liquor_product_id, o]));
  const rows = body.items.map((item) =>
    rowFromItem(item, observations.get(item.id), containerTypeLabel),
  );
  const sections = body.sections.map((s) => ({ id: s.id, name: s.name }));
  const tabs = buildTabs(rows, sections);
  // Walk the shelf from the first section that still has something to count; with no sections, from All.
  const firstOpen =
    sections.length === 0
      ? undefined
      : tabs.find((tab) => tab.id !== ALL_TAB && tab.total > 0 && tab.counted < tab.total);
  return {
    blind: body.session.blind_mode,
    assignedCounterEmployeeId: body.session.assigned_counter_employee_id,
    assignedCounterName: ctx.actorName(body.session.assigned_counter_display_name),
    rows,
    sections,
    filters: { tabId: firstOpen?.id ?? ALL_TAB, type: null, subtype: null, sort: "location" },
    currentId: undefined,
    layout: readCountLayoutPreference(),
    showLast: true,
    kegTaps: new Map(),
    view: "count",
    reviewRows: [],
    reviewFilter: "differences",
    saving: new Set(),
    inflight: new Set(),
    busy: false,
  };
}

/** Opens the table for a count session. False when it could not be loaded (a toast says why). */
export async function openCountTable(
  ctx: CountTableContext,
  sessionId: string,
  /** INVDESIGN1-S2: the Inventory home opens the count in the area layout, on one area, optionally on one item. Nothing is
   * remembered on the device by this; the device's own layout choice is untouched. */
  options: { layout?: "area" | "table"; areaId?: string | null; itemId?: string } = {},
): Promise<boolean> {
  const s = ctx.session();
  if (s === undefined) return false;
  const token = ++sequence;
  const result = await getCountSessionTable(s.apiBaseUrl, s.tenantId, sessionId);
  if (token !== sequence || !ctx.isCurrent()) return false;
  if (result.status !== 200) {
    ctx.toast(
      plainCountMessage(result.body.message ?? result.body.error, "Could not load this count."),
      "danger",
    );
    return false;
  }
  teardownDom();
  ctx.mount.replaceChildren();
  state = {
    ...applyTable(result.body, ctx),
    ctx,
    sessionId,
    root: ctx.mount,
  };
  ctx.mount.hidden = false;
  ctx.lockScroll();
  if (options.layout !== undefined) state.layout = options.layout;
  if (state.layout === "area") {
    const wanted = options.areaId === undefined ? undefined : (options.areaId ?? UNASSIGNED_TAB);
    const itemRow = options.itemId === undefined ? undefined : state.rows.find((r) => r.id === options.itemId);
    enterArea(state, itemRow === undefined ? wanted : tabIdForRow(itemRow));
    if (itemRow !== undefined) state.currentId = itemRow.id;
  }
  renderAll();
  ctx.publishRoute();
  return true;
}

function teardownDom(): void {
  if (state === undefined) return;
  const { ctx, root } = state;
  root.hidden = true;
  root.replaceChildren();
  ctx.unlockScroll();
  state = undefined;
}

/** Leaves the screen with the count still open: every row is already saved. */
export function closeCountTable(): void {
  if (state === undefined) return;
  sequence += 1;
  const { ctx } = state;
  teardownDom();
  ctx.publishRoute();
}

// ---------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------

function renderAll(): void {
  const t = state;
  if (t === undefined) return;
  const keepScroll = t.root.querySelector<HTMLElement>(".ca-list")?.scrollTop;
  t.root.replaceChildren();
  t.root.dataset["view"] = t.view;
  t.root.dataset["layout"] = t.layout;
  t.root.append(t.view === "review" ? buildReviewScreen(t) : buildCountScreen(t));
  const list = t.root.querySelector<HTMLElement>(".ca-list");
  if (list !== null) {
    if (keepScroll !== undefined) list.scrollTop = keepScroll;
    // A tap that changes which item is active brings that item into view; any other redraw leaves the list where it was.
    t.root.querySelector(".ca-active, .ca-keg.is-active")?.scrollIntoView({ block: "nearest" });
  }
}

/** Makes `areaId` (or the first area with something left) the area on screen, with its first uncounted item active. */
function enterArea(t: TableState, areaId: string | undefined): void {
  const tabs = areaTabs(t.rows, t.sections);
  const wanted = areaId ?? t.filters.tabId;
  const id = tabs.some((tab) => tab.id === wanted) ? wanted : firstOpenArea(tabs);
  if (id === undefined) return;
  t.filters = { ...t.filters, tabId: id };
  t.currentId = nextUncountedId(areaRows(t.rows, id), undefined);
}

function areaHost(t: TableState): AreaHost {
  return {
    rows: t.rows,
    sections: t.sections,
    blind: t.blind,
    mine: mine(t),
    holderName: t.assignedCounterName,
    canTakeOver: t.ctx.hasPermission("inv.count_session.reassign"),
    canBlind: t.ctx.hasPermission("inv.count_session.run"),
    busy: t.busy,
    areaId: t.filters.tabId,
    activeId: t.currentId,
    showLast: t.showLast,
    kegTaps: t.kegTaps,
    onExit: () => void exitTable(),
    onReview: () => void openReview(t),
    onTakeOver: () => void takeOver(t),
    onMenu: (anchor) => openAreaMenu(t, anchor),
    onSelectArea: (id) => {
      enterArea(t, id);
      renderAll();
      const list = t.root.querySelector<HTMLElement>(".ca-list");
      if (list !== null) list.scrollTop = 0;
    },
    onActivate: (itemId) => {
      t.currentId = itemId;
      renderAll();
    },
    onToggleLast: () => {
      t.showLast = !t.showLast;
      renderAll();
    },
    onEntry: (row, entry) => change(t, row, entry),
    onKeg: (row, count) => changeKeg(t, row, count),
  };
}

/** The same save as a table entry, for a keg counted by fraction: on tap plus full spares, in kegs. */
function changeKeg(t: TableState, row: TableRow, count: KegCount): void {
  t.kegTaps.set(row.id, count.tapQuarters);
  change(t, row, kegEntry(count));
}

function openAreaMenu(t: TableState, anchor: HTMLElement): void {
  const list = el("div", "ct-menu");
  list.dataset["testid"] = "count-area-more-menu";
  list.append(
    button("ct-menu-item", "Review", "count-area-review-option", () => {
      dismissPopover();
      void openReview(t);
    }),
  );
  if (t.ctx.hasPermission("inv.count_session.run")) {
    list.append(
      button(
        "ct-menu-item",
        t.blind ? "Show quantities" : "Blind count",
        "count-area-blind-option",
        () => {
          dismissPopover();
          void toggleBlind(t);
        },
      ),
    );
  }
  list.append(
    button("ct-menu-item", "Table view", "count-area-table-option", () => {
      dismissPopover();
      t.layout = "table";
      writeCountLayoutPreference("table");
      renderAll();
    }),
    button("ct-menu-item", "One at a time", "count-area-walk-option", () => {
      dismissPopover();
      void switchToWalk(t);
    }),
  );
  showPopover(anchor, list);
}

function mine(t: TableState): boolean {
  return t.assignedCounterEmployeeId === t.ctx.session()?.userId;
}

function buildCountScreen(t: TableState): HTMLElement {
  if (t.layout === "area") return buildAreaScreen(areaHost(t));
  const screen = el("div", "ct-screen");
  screen.append(buildTop(t), buildTabsNav(t), buildTools(t), buildTable(t), buildFooter(t));
  return screen;
}

function progressBar(counted: number, total: number, className: string): HTMLElement {
  const track = el("div", className);
  track.setAttribute("aria-hidden", "true");
  const fill = el("div", `${className}-fill`);
  fill.style.width = total === 0 ? "0%" : `${Math.round((counted / total) * 100)}%`;
  track.append(fill);
  return track;
}

function buildTop(t: TableState): HTMLElement {
  const top = el("header", "ct-top");
  const exit = button("ct-exit", "‹ Save and exit", "count-table-exit", () => exitTable());
  const title = el("h2", "ct-title", "Count");
  const totals = countedTotals(t.rows);
  const progress = el("div", "ct-progress");
  progress.dataset["testid"] = "count-table-progress";
  progress.setAttribute("aria-live", "polite");
  const count = el("strong", undefined, String(totals.counted));
  progress.append(count, el("span", undefined, ` of ${totals.total} counted`));
  const bar = progressBar(totals.counted, totals.total, "ct-bar");
  const spacer = el("div", "ct-spacer");
  top.append(exit, title, progress, bar, spacer);

  if (!mine(t)) {
    const holder = el("span", "ct-holder", t.assignedCounterName);
    holder.dataset["testid"] = "count-table-holder";
    top.append(holder);
    if (t.ctx.hasPermission("inv.count_session.reassign")) {
      top.append(
        button(
          "ct-btn ct-btn-primary",
          "Take over",
          "count-table-takeover",
          () => void takeOver(t),
        ),
      );
    }
  }
  const more = button("ct-icon-btn", "⋯", "count-table-more", () => openMoreMenu(more), "More");
  top.append(more);
  if (t.ctx.hasPermission("inv.count_session.run")) {
    const blind = el("button", `ct-switch${t.blind ? " is-on" : ""}`);
    blind.type = "button";
    blind.dataset["testid"] = "count-table-blind";
    blind.setAttribute("role", "switch");
    blind.setAttribute("aria-checked", String(t.blind));
    blind.disabled = t.busy;
    const knob = el("span", "ct-switch-knob");
    knob.setAttribute("aria-hidden", "true");
    blind.append(knob, el("span", "ct-switch-label", "Blind"));
    blind.addEventListener("click", () => void toggleBlind(t));
    top.append(blind);
  }
  const review = button(
    "ct-btn ct-btn-primary",
    "Review",
    "count-table-review",
    () => void openReview(t),
  );
  review.disabled = t.busy;
  top.append(review);
  return top;
}

function buildTabsNav(t: TableState): HTMLElement {
  const nav = el("nav", "ct-tabs");
  nav.setAttribute("aria-label", "Locations");
  for (const tab of buildTabs(t.rows, t.sections)) {
    const tabButton = el("button", `ct-tab${tab.id === t.filters.tabId ? " is-active" : ""}`);
    tabButton.type = "button";
    tabButton.dataset["testid"] = `count-table-tab-${tab.id}`;
    tabButton.setAttribute("aria-pressed", String(tab.id === t.filters.tabId));
    tabButton.append(el("span", "ct-tab-name", tab.label));
    const tabCount = el("span", "ct-tab-count", tabCountText(tab));
    tabCount.dataset["testid"] = `count-table-tab-count-${tab.id}`;
    tabButton.append(tabCount);
    tabButton.addEventListener("click", () => {
      t.filters = { ...t.filters, tabId: tab.id };
      renderAll();
    });
    nav.append(tabButton);
  }
  return nav;
}

function buildTools(t: TableState): HTMLElement {
  const tools = el("div", "ct-tools");
  const typeOptions = pickerValues(t.rows, "itemKind", (v) => t.ctx.itemKindLabel(v));
  const typePicker = pickerButton(
    "Type",
    t.filters.type === null ? "All" : t.ctx.itemKindLabel(t.filters.type),
    "count-table-type",
    typeOptions,
    t.filters.type,
    (value) => {
      // A subtype that does not exist under the new type is dropped, never left filtering to nothing.
      const remaining = pickerValues(t.rows, "category", categoryLabel, { itemKind: value });
      const keepSubtype =
        t.filters.subtype !== null && remaining.some((o) => o.value === t.filters.subtype);
      t.filters = { ...t.filters, type: value, subtype: keepSubtype ? t.filters.subtype : null };
      renderAll();
    },
  );
  const subtypeOptions = pickerValues(t.rows, "category", categoryLabel, {
    itemKind: t.filters.type,
  });
  const subtypePicker = pickerButton(
    "Subtype",
    t.filters.subtype === null ? "All" : categoryLabel(t.filters.subtype),
    "count-table-subtype",
    subtypeOptions,
    t.filters.subtype,
    (value) => {
      t.filters = { ...t.filters, subtype: value };
      renderAll();
    },
  );
  tools.append(typePicker, subtypePicker);

  const sorts: { id: TableSort; label: string }[] = [
    { id: "location", label: "Location" },
    { id: "name", label: "Name" },
  ];
  // Last count does not exist in a blind count, so neither does sorting by it.
  if (!t.blind) sorts.push({ id: "last", label: "Last count" });
  const group = el("div", "ct-segment");
  group.setAttribute("role", "group");
  group.setAttribute("aria-label", "Sort");
  for (const sort of sorts) {
    const option = button(
      `ct-segment-btn${t.filters.sort === sort.id ? " is-active" : ""}`,
      sort.label,
      `count-table-sort-${sort.id}`,
      () => {
        t.filters = { ...t.filters, sort: sort.id };
        renderAll();
      },
    );
    option.setAttribute("aria-pressed", String(t.filters.sort === sort.id));
    group.append(option);
  }
  tools.append(group, el("div", "ct-spacer"));
  tools.append(button("ct-pill", "Find item", "count-table-find", () => openFind(t)));
  return tools;
}

function pickerButton(
  label: string,
  shown: string,
  testId: string,
  options: { value: string; label: string }[],
  selected: string | null,
  onPick: (value: string | null) => void,
): HTMLButtonElement {
  const trigger = el("button", "ct-pill ct-picker");
  trigger.type = "button";
  trigger.dataset["testid"] = testId;
  trigger.append(
    el("small", undefined, label),
    el("span", "ct-picker-value", shown),
    el("span", "ct-caret", "▾"),
  );
  trigger.addEventListener("click", () => {
    const list = el("div", "ct-menu");
    list.dataset["testid"] = `${testId}-menu`;
    const entries = [{ value: null as string | null, label: "All" }, ...options];
    for (const option of entries) {
      const item = button(
        `ct-menu-item${option.value === selected ? " is-active" : ""}`,
        option.label,
        `${testId}-option`,
        () => {
          dismissPopover();
          onPick(option.value);
        },
      );
      item.dataset["value"] = option.value ?? "";
      list.append(item);
    }
    showPopover(trigger, list);
  });
  return trigger;
}

// ---------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------

function buildTable(t: TableState): HTMLElement {
  const wrap = el("div", `ct-table${t.blind ? " no-last" : ""}`);
  wrap.dataset["testid"] = "count-table";
  const head = el("div", "ct-head");
  for (const label of t.blind
    ? ["", "Item", "Size", "Cases", "Bottles", "Part", "Total", ""]
    : ["", "Item", "Size", "Last count", "Cases", "Bottles", "Part", "Total", ""]) {
    head.append(el("span", undefined, label));
  }
  const body = el("div", "ct-rows");
  body.dataset["testid"] = "count-table-rows";
  const rows = visibleRows(t.rows, t.filters);
  if (rows.length === 0) {
    const empty = el("p", "ct-empty", "No items match these filters. Clear a filter to see more.");
    empty.dataset["testid"] = "count-table-empty";
    body.append(empty);
  }
  for (const row of rows) body.append(buildRow(t, row));
  wrap.append(head, body);
  return wrap;
}

function stepper(
  t: TableState,
  row: TableRow,
  field: EntryField,
  value: number | null,
): HTMLElement {
  const locked = !mine(t);
  const box = el("div", "ct-stepper");
  box.dataset["testid"] = `count-row-${field}`;
  const label = field === "cases" ? "cases" : "bottles";
  const minus = button(
    "ct-step",
    "−",
    `count-row-${field}-minus`,
    () => change(t, row, stepEntry(row.entry, field, -1)),
    `Fewer ${label}, ${row.name}`,
  );
  minus.disabled = locked || value === null || value === 0;
  const shown = el("span", "ct-step-value", value === null ? "–" : String(value));
  shown.dataset["testid"] = `count-row-${field}-value`;
  const plus = button(
    "ct-step ct-step-plus",
    "+",
    `count-row-${field}-plus`,
    () => change(t, row, stepEntry(row.entry, field, 1)),
    `More ${label}, ${row.name}`,
  );
  plus.disabled = locked;
  box.append(minus, shown, plus);
  return box;
}

function buildRow(t: TableState, row: TableRow): HTMLElement {
  const counted = row.entry !== null;
  const node = el(
    "div",
    `ct-row${counted ? " is-counted" : ""}${t.currentId === row.id ? " is-current" : ""}${t.blind ? " no-last" : ""}`,
  );
  node.dataset["testid"] = "count-table-row";
  node.dataset["itemId"] = row.id;
  node.dataset["itemName"] = row.name;

  const marker = el("span", `ct-marker${counted ? " is-counted" : ""}`, counted ? "✓" : "");
  marker.dataset["testid"] = "count-row-counted";
  marker.dataset["counted"] = String(counted);
  marker.setAttribute("role", "img");
  marker.setAttribute("aria-label", counted ? "Counted" : "Not counted");

  const item = el("div", "ct-item");
  const name = el("strong", "ct-name", row.name);
  name.dataset["testid"] = "count-row-name";
  item.append(name, el("span", "ct-sub", categoryLabel(row.category)));

  const size = el("div", "ct-size");
  size.append(el("span", undefined, row.sizeText ?? "-"));
  if (row.caseSize !== null) size.append(el("small", undefined, `Case of ${row.caseSize}`));

  node.append(marker, item, size);

  if (row.lastBottles !== undefined) {
    const last = el("div", "ct-last", row.lastBottles ?? "-");
    last.dataset["testid"] = "count-row-last";
    node.append(last);
  }

  if (row.caseSize === null) {
    const none = el("div", "ct-stepper ct-stepper-na", "-");
    none.dataset["testid"] = "count-row-cases";
    node.append(none);
  } else {
    node.append(stepper(t, row, "cases", row.entry === null ? null : row.entry.cases));
  }
  node.append(stepper(t, row, "bottles", row.entry === null ? null : row.entry.bottles));

  const locked = !mine(t);
  if (row.entry === null) {
    const zero = button(
      "ct-part ct-part-none",
      "None",
      "count-row-none",
      () => change(t, row, zeroEntry()),
      `Count zero, ${row.name}`,
    );
    zero.disabled = locked;
    node.append(zero);
  } else {
    const tenths = row.entry.tenths;
    const part = el("button", "ct-part");
    part.type = "button";
    part.dataset["testid"] = "count-row-part";
    part.disabled = locked;
    part.setAttribute("aria-label", `Part of a bottle, ${row.name}`);
    part.append(
      el("span", "ct-part-value", tenths === 0 ? "0" : `.${tenths}`),
      el("small", undefined, " part"),
    );
    part.addEventListener("click", () => openPartPicker(t, row, part));
    node.append(part);
  }

  const total = el("div", "ct-total", entryTotalText(row.entry, row.caseSize) ?? "–");
  total.dataset["testid"] = "count-row-total";
  node.append(total);

  // Moving an item changes the saved shelf order for every later count, so it needs the section permission.
  if (t.ctx.hasPermission("inv.count_section.manage")) {
    node.append(
      button(
        "ct-icon-btn",
        "⋮",
        "count-row-more",
        () => openMoveSheet(t, row.id),
        `Move ${row.name}`,
      ),
    );
  } else {
    node.append(el("span"));
  }
  return node;
}

function buildFooter(t: TableState): HTMLElement {
  const foot = el("footer", "ct-foot");
  const tabs = buildTabs(t.rows, t.sections);
  const active = tabs.find((tab) => tab.id === t.filters.tabId) ?? tabs[0];
  if (active === undefined) return foot;
  const label = el("div", "ct-foot-label");
  label.dataset["testid"] = "count-table-section-progress";
  label.append(
    el("strong", undefined, active.label),
    el("span", undefined, ` ${tabCountText(active)}`),
  );
  foot.append(label, progressBar(active.counted, active.total, "ct-bar ct-bar-wide"));
  foot.append(el("div", "ct-spacer"));
  const next = nextTab(tabs, active.id);
  foot.append(
    next === undefined
      ? button("ct-btn", "Review", "count-table-next", () => void openReview(t))
      : button("ct-btn", `Next: ${next.label} ›`, "count-table-next", () => {
          t.filters = { ...t.filters, tabId: next.id };
          renderAll();
        }),
  );
  return foot;
}

/** Redraws one row in place and the counters, so a tap never scrolls the table. */
function refreshAfterChange(t: TableState, row: TableRow): void {
  if (t.view !== "count") return;
  if (t.layout === "area") {
    renderAll();
    return;
  }
  t.root.querySelector(".ct-row.is-current")?.classList.remove("is-current");
  const existing = t.root.querySelector<HTMLElement>(
    `[data-testid="count-table-row"][data-item-id="${CSS.escape(row.id)}"]`,
  );
  if (existing !== null) existing.replaceWith(buildRow(t, row));
  t.root.querySelector(".ct-top")?.replaceWith(buildTop(t));
  t.root.querySelector(".ct-tabs")?.replaceWith(buildTabsNav(t));
  t.root.querySelector(".ct-foot")?.replaceWith(buildFooter(t));
}

// ---------------------------------------------------------------------
// Entry and saving
// ---------------------------------------------------------------------

function change(t: TableState, row: TableRow, entry: TableEntry): void {
  if (!mine(t)) return;
  dismissPopover();
  row.entry = entry;
  t.currentId = row.id;
  refreshAfterChange(t, row);
  save(t, row);
}

/** Saves the row's latest entry. Taps made while a save is in flight are coalesced: only the newest entry is sent next. */
function save(t: TableState, row: TableRow): void {
  if (t.saving.has(row.id)) return;
  t.saving.add(row.id);
  const token = sequence;
  const run = (async () => {
    try {
      for (;;) {
        const target = row.entry;
        if (target === null || entriesEqual(target, row.savedEntry)) return;
        const s = t.ctx.session();
        if (s === undefined) return;
        // A keg counted by quarters is a hundredth finer than the table's tenths: it goes through the plain count route as
        // kegs in two decimals (the item's own container, stored in ounces by KEGCOUNT1). Everything else is the table's.
        const result =
          (target.hundredths ?? 0) > 0
            ? await recordCount(s.apiBaseUrl, s.tenantId, t.sessionId, {
                idempotencyKey: crypto.randomUUID(),
                liquorProductId: row.id,
                countedQuantity: formatHundredths(totalHundredths(target, row.caseSize)),
                unit: "bottle",
                recordedByEmployeeId: s.employeeId,
                ...(row.sectionId === null ? {} : { countSectionId: row.sectionId }),
              })
            : await recordCountTableEntry(s.apiBaseUrl, s.tenantId, t.sessionId, {
                idempotencyKey: crypto.randomUUID(),
                liquorProductId: row.id,
                cases: target.cases,
                bottles: target.bottles,
                tenths: target.tenths,
                ...(row.sectionId === null ? {} : { countSectionId: row.sectionId }),
              });
        if (token !== sequence || state !== t) return;
        if (result.status === 200 || result.status === 201) {
          row.savedEntry = target;
          if (entriesEqual(row.entry, target)) return;
          continue;
        }
        // Refused: the row goes back to what the server holds, and the counter is told in plain words.
        row.entry = row.savedEntry;
        t.ctx.toast(
          result.body.error === "count_session_wrong_counter"
            ? countHeldByOtherMessage(
                t.assignedCounterName,
                t.ctx.hasPermission("inv.count_session.reassign"),
              )
            : plainCountMessage(result.body.message, "Could not save that count. Try again."),
          "danger",
        );
        refreshAfterChange(t, row);
        return;
      }
    } finally {
      t.saving.delete(row.id);
    }
  })();
  t.inflight.add(run);
  void run.finally(() => t.inflight.delete(run));
}

async function settleSaves(t: TableState): Promise<void> {
  while (t.inflight.size > 0) await Promise.all([...t.inflight]);
}

async function exitTable(): Promise<void> {
  const t = state;
  if (t === undefined) return;
  await settleSaves(t);
  if (state !== t) return;
  const { ctx } = t;
  closeCountTable();
  ctx.onExit();
}

async function takeOver(t: TableState): Promise<void> {
  const taken = await t.ctx.takeOver(t.sessionId, t.assignedCounterName);
  if (taken === undefined || state !== t) return;
  t.assignedCounterEmployeeId = taken.assigned_counter_employee_id;
  t.assignedCounterName = t.ctx.actorName(taken.assigned_counter_display_name);
  renderAll();
}

// ---------------------------------------------------------------------
// Blind
// ---------------------------------------------------------------------

async function toggleBlind(t: TableState): Promise<void> {
  const s = t.ctx.session();
  if (s === undefined || t.busy) return;
  const token = sequence;
  const goBlind = !t.blind;
  t.busy = true;
  renderAll();
  const fail = (message: string): void => {
    t.busy = false;
    t.ctx.toast(message, "danger");
    if (state === t) renderAll();
  };
  await settleSaves(t);
  const result = await setCountSessionMode(s.apiBaseUrl, s.tenantId, t.sessionId, {
    idempotencyKey: crypto.randomUUID(),
    blindMode: goBlind,
    changedByEmployeeId: s.employeeId,
  });
  if (current(token) !== t) return;
  if (result.status !== 200 && result.status !== 201) {
    fail(
      goBlind
        ? "Could not hide quantities. This count is still quantity-visible."
        : "Could not reveal quantities. This count is still blind.",
    );
    return;
  }
  if (goBlind) {
    // Every last count leaves memory before the next frame is drawn.
    for (const row of t.rows) row.lastBottles = undefined;
    t.blind = true;
    if (t.filters.sort === "last") t.filters = { ...t.filters, sort: "location" };
    t.busy = false;
    renderAll();
    return;
  }
  const table = await getCountSessionTable(s.apiBaseUrl, s.tenantId, t.sessionId);
  if (current(token) !== t) return;
  if (table.status !== 200) {
    // The audited mode says visible but the figures did not load: stay blind on screen, nothing shown.
    fail("Quantities did not load. Try again.");
    return;
  }
  const byId = new Map(table.body.items.map((item) => [item.id, item]));
  for (const row of t.rows) {
    const item = byId.get(row.id);
    row.lastBottles =
      item !== undefined && "lastCount" in item ? (item.lastCount?.inBottles ?? null) : null;
  }
  t.blind = false;
  t.busy = false;
  renderAll();
}

// ---------------------------------------------------------------------
// Popovers and sheets
// ---------------------------------------------------------------------

function dismissPopover(): void {
  state?.root.querySelector(".ct-popover-layer")?.remove();
}

function showPopover(anchor: HTMLElement, content: HTMLElement): void {
  const t = state;
  if (t === undefined) return;
  dismissPopover();
  const layer = el("div", "ct-popover-layer");
  const backdrop = el("button", "ct-popover-backdrop");
  backdrop.type = "button";
  backdrop.dataset["testid"] = "count-table-popover-dismiss";
  backdrop.setAttribute("aria-label", "Close");
  backdrop.addEventListener("click", dismissPopover);
  const pop = el("div", "ct-popover");
  pop.append(content);
  layer.append(backdrop, pop);
  t.root.append(layer);
  const rect = anchor.getBoundingClientRect();
  const box = pop.getBoundingClientRect();
  const margin = 8;
  const left = Math.min(Math.max(margin, rect.left), window.innerWidth - box.width - margin);
  const below = rect.bottom + margin;
  const top =
    below + box.height > window.innerHeight - margin
      ? Math.max(margin, rect.top - box.height - margin)
      : below;
  pop.style.left = `${Math.round(left)}px`;
  pop.style.top = `${Math.round(top)}px`;
}

function openMoreMenu(anchor: HTMLElement): void {
  const t = state;
  if (t === undefined) return;
  const list = el("div", "ct-menu");
  list.dataset["testid"] = "count-table-more-menu";
  list.append(
    button("ct-menu-item", "Count by area", "count-table-area-option", () => {
      dismissPopover();
      t.layout = "area";
      writeCountLayoutPreference("area");
      enterArea(t, undefined);
      renderAll();
    }),
    button("ct-menu-item", "One at a time", "count-table-walk-option", () => {
      dismissPopover();
      void switchToWalk(t);
    }),
  );
  showPopover(anchor, list);
}

async function switchToWalk(t: TableState): Promise<void> {
  await settleSaves(t);
  if (state !== t) return;
  const { ctx, sessionId } = t;
  writeCountViewPreference("walk");
  closeCountTable();
  ctx.openWalk(sessionId);
}

function openPartPicker(t: TableState, row: TableRow, anchor: HTMLElement): void {
  const box = el("div", "ct-part-picker");
  box.dataset["testid"] = "count-part-picker";
  box.append(el("p", "ct-part-title", "Part of a bottle"));
  const grid = el("div", "ct-part-grid");
  for (const tenth of TENTHS_OPTIONS) {
    const option = button(
      `ct-part-option${row.entry?.tenths === tenth ? " is-active" : ""}`,
      tenth === 0 ? "0" : `.${tenth}`,
      `count-part-option-${tenth}`,
      () => change(t, row, setEntryTenths(row.entry, tenth)),
      tenth === 0 ? "No part bottle" : `${tenth} tenths`,
    );
    grid.append(option);
  }
  box.append(grid);
  showPopover(anchor, box);
}

function openSheet(
  t: TableState,
  testId: string,
  title: string,
  subtitle: string | undefined,
): { sheet: HTMLElement; body: HTMLElement; close: () => void } {
  const layer = el("div", "ct-sheet-layer");
  layer.dataset["testid"] = `${testId}-layer`;
  const backdrop = el("div", "ct-sheet-backdrop");
  const sheet = el("section", "ct-sheet");
  sheet.dataset["testid"] = testId;
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-label", title);
  const close = (): void => layer.remove();
  const head = el("header", "ct-sheet-head");
  const heading = el("div", "ct-sheet-heading");
  const titleEl = el("h3", "ct-sheet-title", title);
  titleEl.dataset["testid"] = `${testId}-title`;
  heading.append(titleEl);
  if (subtitle !== undefined) {
    const sub = el("span", "ct-sheet-sub", subtitle);
    sub.dataset["testid"] = `${testId}-subtitle`;
    heading.append(sub);
  }
  head.append(heading);
  const body = el("div", "ct-sheet-body");
  sheet.append(head, body);
  layer.append(backdrop, sheet);
  t.root.append(layer);
  return { sheet: head, body, close };
}

// ---------------------------------------------------------------------
// Move sheet
// ---------------------------------------------------------------------

function openMoveSheet(t: TableState, itemId: string): void {
  dismissPopover();
  const draw = (): void => {
    t.root.querySelector('[data-testid="count-move-sheet-layer"]')?.remove();
    const row = t.rows.find((r) => r.id === itemId);
    if (row === undefined) return;
    const position = shelfPosition(t.rows, itemId);
    const sheet = openSheet(
      t,
      "count-move-sheet",
      row.name,
      position === undefined
        ? categoryLabel(row.category)
        : `${categoryLabel(row.category)} · ${position.index} of ${position.total} in ${position.sectionName}`,
    );
    sheet.sheet.append(button("ct-btn ct-btn-primary", "Done", "count-move-done", sheet.close));

    sheet.body.append(el("p", "ct-sheet-label", "Section"));
    const chips = el("div", "ct-chips");
    const choices: { id: string | null; label: string }[] = [
      ...t.sections.map((s) => ({ id: s.id as string | null, label: s.name })),
      { id: null, label: "Unassigned" },
    ];
    for (const choice of choices) {
      const selected = (row.sectionId ?? null) === choice.id;
      const chip = button(
        `ct-chip${selected ? " is-active" : ""}`,
        choice.label,
        `count-move-section-${choice.id ?? UNASSIGNED_TAB}`,
        () => {
          if (selected || t.busy) return;
          void placementCall(t, () =>
            setCountPlacement(t.ctx.session()!.apiBaseUrl, t.ctx.session()!.tenantId, itemId, {
              countSectionId: choice.id,
            }),
          ).then(draw);
        },
      );
      chip.setAttribute("aria-pressed", String(selected));
      chips.append(chip);
    }
    sheet.body.append(chips);

    sheet.body.append(el("p", "ct-sheet-label", "Place in section"));
    const moves = el("div", "ct-chips");
    const atStart = position === undefined || position.index <= 1;
    const atEnd = position === undefined || position.index >= position.total;
    const moveButtons: { id: CountPlacementMove; label: string; disabled: boolean }[] = [
      { id: "up", label: "↑ Move up", disabled: atStart },
      { id: "down", label: "↓ Move down", disabled: atEnd },
      { id: "top", label: "To top", disabled: atStart },
      { id: "bottom", label: "To bottom", disabled: atEnd },
    ];
    for (const move of moveButtons) {
      const node = button("ct-chip", move.label, `count-move-${move.id}`, () => {
        if (t.busy) return;
        void placementCall(t, () =>
          moveCountPlacement(
            t.ctx.session()!.apiBaseUrl,
            t.ctx.session()!.tenantId,
            itemId,
            move.id,
          ),
        ).then(draw);
      });
      node.disabled = move.disabled || t.busy;
      moves.append(node);
    }
    sheet.body.append(moves);
  };
  draw();
}

/** Runs a placement write, then re-reads the saved order so the table and the sheet show what is stored. */
async function placementCall(
  t: TableState,
  write: () => Promise<{ status: number; body: { message?: string; error?: string } }>,
): Promise<void> {
  const s = t.ctx.session();
  if (s === undefined) return;
  const token = sequence;
  t.busy = true;
  const result = await write();
  if (current(token) !== t) return;
  if (result.status !== 200 && result.status !== 201) {
    t.busy = false;
    t.ctx.toast(plainCountMessage(result.body.message, "Could not move that item."), "danger");
    return;
  }
  const placements = await listCountPlacements(s.apiBaseUrl, s.tenantId);
  if (current(token) !== t) return;
  t.busy = false;
  if (placements.status !== 200) {
    t.ctx.toast("Could not reload the shelf order.", "danger");
    return;
  }
  const byId = new Map(placements.body.items.map((item, index) => [item.id, { item, index }]));
  for (const row of t.rows) {
    const found = byId.get(row.id);
    if (found === undefined) continue;
    row.sectionId = found.item.countSectionId;
    row.sectionName = found.item.countSectionName;
  }
  t.rows.sort(
    (a, b) =>
      (byId.get(a.id)?.index ?? Number.MAX_SAFE_INTEGER) -
      (byId.get(b.id)?.index ?? Number.MAX_SAFE_INTEGER),
  );
  // The table behind the sheet follows the new order, keeping its scroll where it was. renderAll rebuilds the root, so
  // the caller's draw() puts the sheet back over the fresh table.
  const scroll = t.root.querySelector<HTMLElement>(".ct-rows")?.scrollTop ?? 0;
  renderAll();
  const fresh = t.root.querySelector<HTMLElement>(".ct-rows");
  if (fresh !== null) fresh.scrollTop = scroll;
}

// ---------------------------------------------------------------------
// Find item
// ---------------------------------------------------------------------

function openFind(t: TableState): void {
  dismissPopover();
  const sheet = openSheet(t, "count-find-sheet", "Find item", undefined);
  sheet.sheet.append(button("ct-btn", "Close", "count-find-close", sheet.close));
  const layout = el("div", "ct-find");
  const list = el("div", "ct-find-list");
  list.dataset["testid"] = "count-find-list";
  const sorted = [...t.rows].sort((a, b) => compareNames(a.name, b.name));
  const firstByLetter = new Map<string, HTMLElement>();
  for (const row of sorted) {
    const entry = el("button", "ct-find-item");
    entry.type = "button";
    entry.dataset["testid"] = "count-find-item";
    entry.dataset["itemId"] = row.id;
    const text = el("span", "ct-find-text");
    text.append(
      el("strong", undefined, row.name),
      el("small", undefined, categoryLabel(row.category)),
    );
    entry.append(text, el("span", "ct-find-section", row.sectionName ?? "Unassigned"));
    entry.addEventListener("click", () => {
      sheet.close();
      jumpToRow(t, row.id);
    });
    const letter = firstLetter(row.name);
    if (!firstByLetter.has(letter)) firstByLetter.set(letter, entry);
    list.append(entry);
  }
  if (sorted.length === 0) list.append(el("p", "ct-empty", "No items to show."));
  const strip = el("div", "ct-az");
  strip.dataset["testid"] = "count-find-strip";
  for (const letter of alphabetLetters(sorted.map((r) => r.name))) {
    strip.append(
      button("ct-az-letter", letter, `count-find-letter-${letter}`, () => {
        firstByLetter.get(letter)?.scrollIntoView({ block: "start" });
      }),
    );
  }
  layout.append(list, strip);
  sheet.body.append(layout);
}

/** Shows an item on its own section's tab, scrolled into view and marked as the current row. */
function jumpToRow(t: TableState, itemId: string): void {
  const row = t.rows.find((r) => r.id === itemId);
  if (row === undefined) return;
  t.view = "count";
  t.filters = { ...t.filters, tabId: tabIdForRow(row), type: null, subtype: null };
  t.currentId = itemId;
  renderAll();
  t.root
    .querySelector(`[data-testid="count-table-row"][data-item-id="${CSS.escape(itemId)}"]`)
    ?.scrollIntoView({ block: "center" });
}

// ---------------------------------------------------------------------
// Review and close
// ---------------------------------------------------------------------

async function openReview(t: TableState): Promise<void> {
  const s = t.ctx.session();
  if (s === undefined || t.busy) return;
  const token = sequence;
  t.busy = true;
  renderAll();
  await settleSaves(t);
  const [review, uncounted, walk] = await Promise.all([
    getCountSessionReview(s.apiBaseUrl, s.tenantId, t.sessionId),
    getCountSessionUncounted(s.apiBaseUrl, s.tenantId, t.sessionId),
    // Expected figures for the items not counted. A blind count has none to read.
    t.blind
      ? Promise.resolve(undefined)
      : getCountSessionWalk(s.apiBaseUrl, s.tenantId, t.sessionId),
  ]);
  if (current(token) !== t) return;
  t.busy = false;
  if (review.status !== 200 || uncounted.status !== 200) {
    t.ctx.toast("Could not load the review. Your saved counts are still here.", "danger");
    renderAll();
    return;
  }
  const expectedById = new Map<string, string | null>();
  if (walk !== undefined && walk.status === 200) {
    for (const item of walk.body.items) {
      expectedById.set(item.id, item.expectedOnHand?.inBottles ?? null);
    }
  }
  const bottleOuncesByName = new Map(t.rows.map((row) => [row.name, row.bottleOunces]));
  t.reviewRows = [
    ...review.body.rows.map((row) => reviewRowFromCounted(row, bottleOuncesByName)),
    ...uncounted.body.items.map((item) =>
      reviewRowFromUncounted(item, expectedById.get(item.id) ?? null),
    ),
  ];
  t.view = "review";
  t.reviewFilter = t.reviewRows.some(isDifference) ? "differences" : "all";
  renderAll();
}

function buildReviewScreen(t: TableState): HTMLElement {
  const screen = el("div", "ct-screen ct-review");
  screen.dataset["testid"] = "count-review-screen";
  const rows = t.reviewRows;
  const counted = rows.filter((row) => row.counted).length;
  const notCounted = rows.length - counted;

  const top = el("header", "ct-top");
  top.append(
    button("ct-exit", "‹ Back to count", "count-review-back", () => {
      t.view = "count";
      renderAll();
    }),
    el("h2", "ct-title", "Review"),
  );
  const progress = el("div", "ct-progress");
  progress.append(
    el("strong", undefined, String(counted)),
    el("span", undefined, ` of ${rows.length} counted`),
  );
  top.append(progress, progressBar(counted, rows.length, "ct-bar"), el("div", "ct-spacer"));
  const close = button("ct-btn ct-btn-primary", "Close count", "count-review-close", () =>
    openCloseConfirm(t, notCounted),
  );
  close.disabled = counted === 0 || t.busy;
  top.append(close);

  // INVDESIGN1-S2 (mockup invdesign1-5): what was expected, what was counted, the gap in dollars and percent, and how many
  // items to check. Dollars come from the server's stock-value valuation and are drawn only when it sent them.
  const cards = el("div", "ct-cards");
  const card = (label: string, value: string, tone: string, testId: string, sub?: string): HTMLElement => {
    const node = el("div", `ct-card ${tone}`);
    node.dataset["testid"] = testId;
    node.append(el("span", "ct-card-label", label), el("strong", "ct-card-value", value));
    if (sub !== undefined) node.append(el("span", "ct-card-sub", sub));
    return node;
  };
  const money = reviewValueTotals(rows);
  const checkCount = rows.filter((row) => row.check).length;
  if (money !== null) {
    const gap = dollarVariance(money.expectedCents, money.countedCents);
    const gapPercent = gap.percent === null ? "" : ` (${signedPercent(gap.percent)})`;
    cards.append(
      card("Expected", formatMoney(gap.expectedCents), "", "count-review-card-expected"),
      card("Counted", formatMoney(gap.countedCents), "", "count-review-card-value"),
      card(
        "Difference",
        `${signedMoney(gap.differenceCents)}${gapPercent}`,
        gap.differenceCents < 0 ? "is-short" : gap.differenceCents > 0 ? "is-over" : "",
        "count-review-card-difference",
        money.unvalued > 0
          ? `${money.unvalued} counted ${money.unvalued === 1 ? "item has" : "items have"} no cost and ${money.unvalued === 1 ? "is" : "are"} left out`
          : undefined,
      ),
    );
  }
  cards.append(
    card(
      "To check",
      `${checkCount} ${checkCount === 1 ? "item" : "items"}`,
      checkCount > 0 ? "is-warn" : "",
      "count-review-card-check",
      `Off by ${COUNT_CHECK_PERCENT}% or more`,
    ),
  );
  const status = el("p", "ct-review-status");
  status.dataset["testid"] = "count-review-status";
  const countedCard = el("span", undefined, `Counted ${counted} of ${rows.length}`);
  countedCard.dataset["testid"] = "count-review-card-counted";
  const notCountedCard = el("span", notCounted > 0 ? "is-warn" : undefined, `Not counted ${notCounted}`);
  notCountedCard.dataset["testid"] = "count-review-card-not-counted";
  status.append(countedCard, notCountedCard);

  const filters = el("div", "ct-segment ct-review-filters");
  const filterDefs: { id: ReviewFilter; label: string; count: number }[] = [
    { id: "all", label: "All", count: rows.length },
    { id: "differences", label: "Differences", count: rows.filter(isDifference).length },
    { id: "not-counted", label: "Not counted", count: notCounted },
  ];
  for (const def of filterDefs) {
    const option = button(
      `ct-segment-btn${t.reviewFilter === def.id ? " is-active" : ""}`,
      `${def.label} ${def.count}`,
      `count-review-filter-${def.id}`,
      () => {
        t.reviewFilter = def.id;
        renderAll();
      },
    );
    option.setAttribute("aria-pressed", String(t.reviewFilter === def.id));
    filters.append(option);
  }
  const bar = el("div", "ct-tools");
  bar.append(filters);

  const table = el("div", "ct-table ct-review-table");
  const head = el("div", "ct-head");
  for (const label of ["Item", "Expected", "Counted", "Off by", "%", "Cost", "Status", ""])
    head.append(el("span", undefined, label));
  const body = el("div", "ct-rows");
  body.dataset["testid"] = "count-review-rows";
  const shown = sortReviewRows(filterReviewRows(rows, t.reviewFilter));
  if (shown.length === 0) {
    const empty = el("p", "ct-empty", "Nothing to review yet. Count an item and it appears here.");
    empty.dataset["testid"] = "count-review-empty";
    body.append(empty);
  }
  for (const row of shown) {
    const node = el("div", "ct-review-row");
    node.dataset["testid"] = "count-review-row";
    node.dataset["itemName"] = row.name;
    node.dataset["status"] = row.counted ? "counted" : "not-counted";
    node.dataset["check"] = row.check ? "true" : "false";
    const item = el("div", "ct-item");
    item.append(
      el("strong", "ct-name", row.name),
      el("span", "ct-sub", row.sectionName ?? "Unassigned"),
    );
    const tone = row.difference !== null && row.difference < 0 ? " is-short" : row.difference !== null && row.difference > 0 ? " is-over" : "";
    const expected = el("div", "ct-figure", row.expected ?? "-");
    expected.dataset["testid"] = "count-review-expected";
    const countedFigure = el("div", "ct-figure", row.countedText ?? "-");
    countedFigure.dataset["testid"] = "count-review-counted";
    const diff = el("div", `ct-figure ct-diff${tone}`, row.differenceText);
    diff.dataset["testid"] = "count-review-difference";
    const percent = el(
      "div",
      `ct-figure ct-diff${tone}`,
      row.counted ? signedPercent(row.percent) : "-",
    );
    percent.dataset["testid"] = "count-review-percent";
    const cost = el(
      "div",
      `ct-figure ct-diff${row.costCents === null ? "" : row.costCents < 0 ? " is-short" : row.costCents > 0 ? " is-over" : ""}`,
      row.costCents === null ? "-" : signedMoney(row.costCents),
    );
    cost.dataset["testid"] = "count-review-cost";
    const verdict = row.counted ? (row.check ? "Check" : "Normal") : "Not counted";
    const flag = el(
      "span",
      `ct-pill-status ${row.check ? "is-check" : row.counted ? "is-counted" : "is-not-counted"}`,
      row.check ? "! Check" : verdict,
    );
    flag.dataset["testid"] = "count-review-flag";
    node.append(item, expected, countedFigure, diff, percent, cost, flag);
    if (row.itemId !== null) {
      const itemId = row.itemId;
      node.append(
        button(
          "ct-btn ct-btn-small",
          row.counted ? "Recount" : "Count",
          row.counted ? "count-review-recount" : "count-review-count",
          () => jumpToRow(t, itemId),
          `${row.counted ? "Recount" : "Count"} ${row.name}`,
        ),
      );
    } else {
      node.append(el("span"));
    }
    body.append(node);
  }
  table.append(head, body);
  screen.append(top, cards, status, bar, table);
  return screen;
}

function openCloseConfirm(t: TableState, notCounted: number): void {
  const counted = t.reviewRows.filter((row) => row.counted).length;
  const sheet = openSheet(
    t,
    "count-close-confirm",
    "Close count",
    `${counted} counted, ${notCounted} not counted`,
  );
  // INVPOWALK1 (F10, design-language rules 3 and 17): say what closing does before it is done. The adjustments are the
  // Differences on the review, the same number the closed-count result reports, and items not counted keep their stock.
  const adjustments = t.reviewRows.filter(isDifference).length;
  const effect =
    adjustments === 0
      ? "No stock changes to post."
      : `${adjustments} ${adjustments === 1 ? "adjustment posts" : "adjustments post"} to stock.`;
  const kept =
    notCounted === 0
      ? ""
      : notCounted === 1
        ? " 1 item not counted keeps its stock as it is."
        : ` ${notCounted} items not counted keep their stock as they are.`;
  const note = el("p", "ct-sheet-note", `${effect}${kept}`);
  note.dataset["testid"] = "count-close-confirm-note";
  sheet.body.append(note);
  const actions = el("div", "ct-sheet-actions");
  const confirm = button("ct-btn ct-btn-primary", "Close count", "count-close-confirm-yes", () => {
    confirm.disabled = true;
    void closeNow(t, sheet.close, () => (confirm.disabled = false));
  });
  actions.append(button("ct-btn", "Cancel", "count-close-confirm-cancel", sheet.close), confirm);
  sheet.body.append(actions);
}

async function closeNow(
  t: TableState,
  closeSheet: () => void,
  reenable: () => void,
): Promise<void> {
  const token = sequence;
  await settleSaves(t);
  const closed = await t.ctx.closeCount(t.sessionId);
  if (current(token) !== t) return;
  if (closed === undefined) {
    reenable();
    return;
  }
  closeSheet();
  const s = t.ctx.session();
  const uncounted =
    s === undefined
      ? undefined
      : await getCountSessionUncounted(s.apiBaseUrl, s.tenantId, t.sessionId);
  if (current(token) !== t) return;
  // The list is read from the server; if that read fails, the review's own not counted rows stand in for it.
  const names =
    uncounted !== undefined && uncounted.status === 200
      ? uncounted.body.items.map((item) => ({ name: item.name, section: item.countSectionName }))
      : t.reviewRows
          .filter((row) => !row.counted)
          .map((row) => ({ name: row.name, section: row.sectionName }));
  showCloseReport(t, closed.adjustmentCount, names);
}

function showCloseReport(
  t: TableState,
  adjustmentCount: number,
  uncounted: { name: string; section: string | null }[],
): void {
  const sheet = openSheet(t, "count-close-report", "Count closed", undefined);
  const summary = el("p", "ct-report-line");
  summary.dataset["testid"] = "count-close-report-adjustments";
  summary.append(
    el("span", undefined, "Adjustments posted"),
    el("strong", undefined, String(adjustmentCount)),
  );
  sheet.body.append(summary);
  sheet.body.append(el("p", "ct-sheet-label", "Not counted"));
  const list = el("div", "ct-report-list");
  list.dataset["testid"] = "count-close-report-uncounted";
  if (uncounted.length === 0) {
    list.append(el("p", "ct-empty", "Every item was counted."));
  }
  for (const item of uncounted) {
    const line = el("div", "ct-report-row");
    line.dataset["testid"] = "count-close-report-uncounted-row";
    line.append(
      el("strong", undefined, item.name),
      el("span", undefined, item.section ?? "Unassigned"),
    );
    list.append(line);
  }
  sheet.body.append(list);
  const done = button("ct-btn ct-btn-primary", "Done", "count-close-report-done", () => {
    const { ctx } = t;
    const closed = closedSummary(t.sessionId, t.reviewRows);
    sequence += 1;
    teardownDom();
    ctx.publishRoute();
    ctx.onClosed(closed);
  });
  sheet.sheet.append(done);
}
